/* eslint-disable jsx-a11y/media-has-caption */
import { Dropdown, Popover } from "antd";
import React, { useEffect, useRef, useState } from "react";
import PauseIconBlue from "../../../icons/js/PauseIconBlue";
import PlayIconJSX from "../../../icons/js/PlayIcon";
import SpeakerUnmuted from "../../../icons/js/SpeakerUnmuted";
import SpeakerMuted from "../../../icons/js/SpeakerMuted";
import RewindIcon from "../../../icons/svg/fast-forward.svg";
import FastForwardIcon from "../../../icons/svg/rewind.svg";
import "../../../styles/audioComponent.css"; // Ensure this path is correct

const speedArray = [
  { label: "0.5x", key: 0.5 },
  { label: "0.75x", key: 0.75 },
  { label: "1x", key: 1 },
  { label: "1.25x", key: 1.25 },
  { label: "1.5x", key: 1.5 },
  { label: "2x", key: 2 },
];

const formatTime = timeInSeconds => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

const AudioComponent = ({ src }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef();
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    const audio = audioRef?.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleSpeedChange = e => {
    const newSpeed = parseFloat(e);
    setSpeed(newSpeed);
    const audio = audioRef?.current;
    audio.playbackRate = newSpeed;
  };

  const handleVolume = value => {
    const newVolume = parseFloat(value);
    setVolume(newVolume);
    const audio = audioRef?.current;
    audio.volume = newVolume;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener("timeupdate", () => {
        setCurrentTime(audio.currentTime);
      });
      audio.addEventListener("loadedmetadata", () => {
        setDuration(audio.duration);
      });
      setIsPlaying(!audio.paused);
      return () => {
        audio.removeEventListener("timeupdate", () => {
          setCurrentTime(audio.currentTime);
        });
        audio.removeEventListener("loadedmetadata", () => {
          setDuration(audio.duration);
        });
      };
    }
  }, []);

  useEffect(() => {
    if (currentTime === duration) {
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [duration, currentTime]);

  const handleSeek = e => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = parseFloat(e.target.value);
      setCurrentTime(audio.currentTime);
    }
  };

  // Rewind audio by 10 seconds
  const handleRewind = () => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = Math.max(0, audio.currentTime - 10);
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Fast forward audio by 10 seconds
  const handleFastForward = () => {
    const audio = audioRef.current;
    if (audio) {
      const newTime = Math.min(duration, audio.currentTime + 10);
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <div className="flex-row-centered flex-wrap sm:flex-nowrap gap-1.5 bg-gray-100 py-1.5 pb-3 sm:pb-1.5 pr-3 pl-2 rounded">
      {/* Rewind button - 10 seconds back */}
      <span
        role="button"
        tabIndex={0}
        onClick={handleRewind}
        onKeyDown={handleRewind}
        className="shrink-0 flex items-center cursor-pointer"
      >
        <img src={RewindIcon} alt="Rewind 10s" width={18} height={18} />
      </span>

      {/* Play/Pause button */}
      <div className="shrink-0 flex items-center">
        {audioRef?.current && !audioRef?.current?.paused ? (
          <PauseIconBlue className="cursor-pointer" onClick={togglePlay} />
        ) : (
          <PlayIconJSX className="cursor-pointer" onClick={togglePlay} />
        )}
      </div>

      {/* Fast forward button - 10 seconds ahead */}
      <span
        role="button"
        tabIndex={0}
        onClick={handleFastForward}
        onKeyDown={handleFastForward}
        className="shrink-0 flex items-center cursor-pointer"
      >
        <img src={FastForwardIcon} alt="Fast Forward 10s" width={18} height={18} />
      </span>

      <audio id="audio" ref={audioRef} src={src} />

      {/* Progress slider */}
      <input
        type="range"
        className="custom-range-slider sm:flex-1 min-w-0 w-full sm:w-auto order-last sm:order-none"
        min="0"
        max={duration}
        step={0.000001}
        value={currentTime}
        onChange={handleSeek}
      />

      {/* Time display */}
      <span className="xs-400 text-gray-500 whitespace-nowrap">{`${formatTime(
        currentTime
      )} / ${formatTime(duration)}`}</span>

      {/* Volume control */}
      <Popover
        trigger="hover"
        content={
          <input
            type="range"
            min={0}
            max={1}
            step={0.0001}
            value={volume}
            className="custom-volume-slider"
            onChange={e => handleVolume(e.target.value)}
          />
        }
      >
        <div className="shrink-0 flex items-center">
          {volume === 0 ? (
            <SpeakerMuted
              onClick={() =>
                setVolume(prev => {
                  if (prev === 0) return 1;
                  return 0;
                })
              }
              className="cursor-pointer"
            />
          ) : (
            <SpeakerUnmuted
              onClick={() =>
                setVolume(prev => {
                  if (prev === 0) return 1;
                  return 0;
                })
              }
              className="cursor-pointer"
            />
          )}
        </div>
      </Popover>

      {/* Playback speed control */}
      <Dropdown menu={{ items: speedArray, onClick: e => handleSpeedChange(e.key) }}>
        <span className="cursor-pointer shrink-0">{speed}x</span>
      </Dropdown>
    </div>
  );
};

export default AudioComponent;
